import React, { useState, useEffect } from 'react';
import { db, auth, handleFirestoreError, OperationType, firebaseConfig } from '../firebase';
import { collection, getDocs, doc, setDoc, deleteDoc, query, orderBy } from 'firebase/firestore';
import { initializeApp } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth';
import { UserPlus, Shield, Mail, Lock, Trash2, Edit2, Check, X, Loader2, School, UserCog } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { SchoolClass } from '../types';

// Secondary Firebase app to create users without logging out the admin
const secondaryApp = initializeApp(firebaseConfig, 'SecondaryApp');
const secondaryAuth = getAuth(secondaryApp);

interface UserProfile {
  uid: string;
  email: string;
  displayName: string;
  role: 'admin' | 'manager' | 'teacher' | 'holiday_manager';
  assignedClassId?: string;
  createdAt: any;
}

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [classes, setClasses] = useState<SchoolClass[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  
  // Form State
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<'admin' | 'manager' | 'teacher' | 'holiday_manager'>('teacher');
  const [assignedClassId, setAssignedClassId] = useState('');
  
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const [deletingId, setDeletingId] = useState<string | null>(null);
  
  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersSnap, classesSnap] = await Promise.all([
        getDocs(query(collection(db, 'users'), orderBy('email'))),
        getDocs(query(collection(db, 'classes'), orderBy('name')))
      ]);
      
      const usersList = usersSnap.docs.map(doc => ({
        uid: doc.id,
        ...doc.data()
      })) as UserProfile[];
      
      const classesList = classesSnap.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name
      })) as SchoolClass[];
      
      setUsers(usersList);
      setClasses(classesList);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage({ text: '', type: '' });
    
    try {
      // 1. Create in Auth (using secondary app)
      const userCredential = await createUserWithEmailAndPassword(secondaryAuth, email, password);
      const uid = userCredential.user.uid;
      
      // 2. Create Profile in Firestore
      const profileData: any = {
        email,
        displayName,
        role,
        createdAt: new Date().toISOString(),
      };
      
      if (role === 'teacher' && assignedClassId) {
        profileData.assignedClassId = assignedClassId;
      }
      
      await setDoc(doc(db, 'users', uid), profileData);
      
      // 3. Cleanup
      await secondaryAuth.signOut(); // Sign out from secondary app
      
      setMessage({ text: 'משתמש נוצר בהצלחה!', type: 'success' });
      setIsAdding(false);
      setEmail('');
      setPassword('');
      setDisplayName('');
      setRole('teacher');
      setAssignedClassId('');
      fetchData();
    } catch (error: any) {
      console.error("Error creating user:", error);
      let errorMsg = `שגיאה: ${error.message}`;
      if (error.code === 'auth/email-already-in-use' || error.message.includes('auth/email-already-in-use')) {
        errorMsg = 'שגיאה: האימייל כבר נמצא בשימוש במערכת. אם המשתמש לא מופיע ברשימה, ייתכן שקיים חשבון ללא פרופיל - פנה למנהל המערכת.';
      } else if (error.code === 'auth/operation-not-allowed' || error.message.includes('auth/operation-not-allowed')) {
        errorMsg = `שגיאה: שיטת ההתחברות באימייל וסיסמה אינה מופעלת ב-Firebase. 
        וודא שאתה בפרויקט הנכון (${firebaseConfig.projectId}) ושמרת את השינויים בלוח הבקרה.`;
      } else if (error.code === 'auth/weak-password' || error.message.includes('auth/weak-password')) {
        errorMsg = 'שגיאה: הסיסמה חלשה מדי. יש להזין לפחות 6 תווים.';
      } else if (error.code === 'auth/invalid-email' || error.message.includes('auth/invalid-email')) {
        errorMsg = 'שגיאה: כתובת האימייל אינה תקינה.';
      }
      setMessage({ text: errorMsg, type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateRole = async (uid: string, newRole: any, newClassId?: string) => {
    try {
      const updateData: any = { role: newRole };
      if (newRole === 'teacher' && newClassId) {
        updateData.assignedClassId = newClassId;
      } else {
        updateData.assignedClassId = null;
      }
      
      await setDoc(doc(db, 'users', uid), updateData, { merge: true });
      setMessage({ text: 'ההרשאות עודכנו', type: 'success' });
      setEditingId(null);
      fetchData();
    } catch (error) {
      console.error("Error updating user:", error);
      setMessage({ text: 'שגיאה בעדכון', type: 'error' });
    }
  };

  const handleDeleteUser = async (uid: string) => {
    setDeletingId(uid);
  };

  const confirmDeleteUser = async () => {
    if (!deletingId) return;
    
    try {
      await deleteDoc(doc(db, 'users', deletingId));
      setMessage({ text: 'הפרופיל נמחק', type: 'success' });
      setDeletingId(null);
      fetchData();
    } catch (error) {
      console.error("Error deleting user:", error);
      setMessage({ text: 'שגיאה במחיקה', type: 'error' });
    }
  };

  return (
    <div className="space-y-6" dir="rtl">
      <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-indigo-50 text-indigo-600 rounded-2xl">
              <Shield size={24} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">ניהול הרשאות ומשתמשים</h2>
              <p className="text-sm text-slate-500">הקמת משתמשים חדשים וניהול תפקידים</p>
            </div>
          </div>
          <button
            onClick={() => setIsAdding(!isAdding)}
            className="flex items-center gap-2 bg-indigo-600 text-white px-5 py-2.5 rounded-2xl hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100 font-bold"
          >
            {isAdding ? <X size={20} /> : <UserPlus size={20} />}
            {isAdding ? 'ביטול' : 'הוספת משתמש'}
          </button>
        </div>

        <AnimatePresence>
          {isAdding && (
            <motion.form
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              onSubmit={handleCreateUser}
              className="bg-slate-50 p-6 rounded-2xl border border-slate-200 overflow-hidden mb-6 space-y-4"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-600 mr-1">שם מלא</label>
                  <input
                    type="text"
                    required
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="w-full px-4 py-2 bg-white border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="ישראל ישראלי"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-600 mr-1">אימייל</label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-2 bg-white border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="user@example.com"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-600 mr-1">סיסמה</label>
                  <input
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-2 bg-white border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="••••••••"
                    minLength={6}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-600 mr-1">תפקיד</label>
                  <select
                    value={role}
                    onChange={(e) => setRole(e.target.value as any)}
                    className="w-full px-4 py-2 bg-white border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="teacher">גננת / מורה</option>
                    <option value="manager">צוות ניהול</option>
                    <option value="holiday_manager">מנהל קייטנה</option>
                    <option value="admin">מנהל מערכת</option>
                  </select>
                </div>
              </div>

              {role === 'teacher' && (
                <div className="space-y-1 max-w-xs">
                  <label className="text-xs font-bold text-slate-600 mr-1">שיוך לגן / כיתה</label>
                  <select
                    required
                    value={assignedClassId}
                    onChange={(e) => setAssignedClassId(e.target.value)}
                    className="w-full px-4 py-2 bg-white border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">בחר גן...</option>
                    {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              )}

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={submitting}
                  className="bg-indigo-600 text-white px-8 py-2 rounded-xl font-bold hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {submitting ? <Loader2 className="animate-spin" size={18} /> : <Check size={18} />}
                  צור משתמש
                </button>
              </div>
            </motion.form>
          )}
        </AnimatePresence>

        <div className="overflow-x-auto">
          <table className="w-full text-right">
            <thead>
              <tr className="border-b border-slate-100 text-slate-400 text-sm">
                <th className="px-4 py-3 font-medium">משתמש</th>
                <th className="px-4 py-3 font-medium">תפקיד</th>
                <th className="px-4 py-3 font-medium">שיוך</th>
                <th className="px-4 py-3 font-medium text-center">פעולות</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {loading ? (
                <tr>
                  <td colSpan={4} className="py-8 text-center text-slate-400">טוען נתונים...</td>
                </tr>
              ) : users.map(user => (
                <tr key={user.uid} className="hover:bg-slate-50/50 transition-colors">
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center text-slate-600 font-bold">
                        {user.displayName?.[0] || user.email[0].toUpperCase()}
                      </div>
                      <div>
                        <div className="font-bold text-slate-900">{user.displayName || 'ללא שם'}</div>
                        <div className="text-xs text-slate-500">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    {editingId === user.uid ? (
                      <select
                        value={user.role}
                        onChange={(e) => handleUpdateRole(user.uid, e.target.value, user.assignedClassId)}
                        className="px-2 py-1 bg-white border border-slate-200 rounded-lg text-sm"
                      >
                        <option value="teacher">גננת / מורה</option>
                        <option value="manager">צוות ניהול</option>
                        <option value="holiday_manager">מנהל קייטנה</option>
                        <option value="admin">מנהל מערכת</option>
                      </select>
                    ) : (
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                        user.role === 'admin' ? 'bg-red-50 text-red-600' :
                        user.role === 'manager' ? 'bg-amber-50 text-amber-600' :
                        user.role === 'holiday_manager' ? 'bg-rose-50 text-rose-600' :
                        'bg-emerald-50 text-emerald-600'
                      }`}>
                        {user.role === 'admin' ? 'מנהל מערכת' :
                         user.role === 'manager' ? 'צוות ניהול' : 
                         user.role === 'holiday_manager' ? 'מנהל קייטנה' : 'גננת / מורה'}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    {user.role === 'teacher' ? (
                      editingId === user.uid ? (
                        <select
                          value={user.assignedClassId || ''}
                          onChange={(e) => handleUpdateRole(user.uid, user.role, e.target.value)}
                          className="px-2 py-1 bg-white border border-slate-200 rounded-lg text-sm"
                        >
                          <option value="">בחר גן...</option>
                          {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                      ) : (
                        <div className="flex items-center gap-1.5 text-slate-600 text-sm">
                          <School size={14} className="text-slate-400" />
                          {classes.find(c => c.id === user.assignedClassId)?.name || 'לא שויך'}
                        </div>
                      )
                    ) : '-'}
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex justify-center gap-2">
                      <button
                        onClick={() => setEditingId(editingId === user.uid ? null : user.uid)}
                        className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
                      >
                        {editingId === user.uid ? <Check size={18} /> : <Edit2 size={18} />}
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user.uid)}
                        className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {message.text && (
        <div className={`fixed bottom-8 left-1/2 -translate-x-1/2 px-6 py-3 rounded-2xl shadow-xl text-white font-bold z-50 flex items-center gap-2 ${
          message.type === 'success' ? 'bg-emerald-600' : 'bg-red-600'
        }`}>
          {message.type === 'success' ? <Check size={18} /> : <X size={18} />}
          {message.text}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {deletingId && (
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full border border-slate-100"
            >
              <div className="w-16 h-16 bg-red-50 text-red-600 rounded-2xl flex items-center justify-center mb-6 mx-auto">
                <Trash2 size={32} />
              </div>
              <h3 className="text-xl font-black text-slate-900 text-center mb-2">מחיקת פרופיל משתמש</h3>
              <p className="text-slate-500 text-center text-sm leading-relaxed mb-8">
                האם אתה בטוח שברצונך למחוק את פרופיל המשתמש? 
                <br />
                <span className="font-bold text-red-500">הערה:</span> המשתמש עדיין יהיה קיים במערכת האימות אך לא תהיה לו גישה לנתונים.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeletingId(null)}
                  className="flex-1 px-6 py-3 bg-slate-100 text-slate-600 rounded-2xl font-bold hover:bg-slate-200 transition-all"
                >
                  ביטול
                </button>
                <button
                  onClick={confirmDeleteUser}
                  className="flex-1 px-6 py-3 bg-red-600 text-white rounded-2xl font-bold hover:bg-red-700 shadow-lg shadow-red-100 transition-all"
                >
                  מחק לצמיתות
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default UserManagement;
